import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '../common/ui/Button';
import BaseCard from '../common/layout/BaseCard';
import { supabase } from '../../lib/supabase';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import rehypeRaw from 'rehype-raw';
import Avatar from '../common/display/Avatar';
import { ShieldCheckIcon } from '@heroicons/react/24/solid';
import { GlobeAltIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import Pill from '../common/display/Pill';
import { Modal } from '../common/ui/Modal';
import { CommunityUpdate } from './types';
import { getPublicUrl } from '../../utils/filestorageUtils';
import { captureAppException } from '../../utils/errorCaptureUtils';
import { captureCustomEvent } from '../../utils/posthogUtils';

// --- Start of useBreakpoint Hook Logic ---
const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

type BreakpointKey = keyof typeof breakpoints | 'xs';

const getDeviceBreakpoint = (width: number): BreakpointKey => {
  if (width < breakpoints.sm) return 'xs';
  if (width < breakpoints.md) return 'sm';
  if (width < breakpoints.lg) return 'md';
  if (width < breakpoints.xl) return 'lg';
  if (width < breakpoints['2xl']) return 'xl';
  return '2xl';
};

const useBreakpoint = (): BreakpointKey => {
  const [breakpoint, setBreakpoint] = useState<BreakpointKey>(() => {
    if (typeof window !== 'undefined') {
      return getDeviceBreakpoint(window.innerWidth);
    }
    return 'lg'; // Default for SSR
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      setBreakpoint(getDeviceBreakpoint(window.innerWidth));
    };
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial call
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return breakpoint;
};
// --- End of useBreakpoint Hook Logic ---

interface CommunityUpdatesProps {
  communityId: string;
}

const customSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    img: [
      ...new Set([
        ...(defaultSchema.attributes?.img || []),
        'src',
        'alt',
        'width',
        'height'
      ])
    ],
  },
};

const formatRelativeTime = (timestamp: string): string => {
  const now = new Date();
  const date = new Date(timestamp);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Now';
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 2592000) {
    const weeks = Math.floor(diffInSeconds / 604800);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (diffInSeconds < 31536000) {
    const months = Math.floor(diffInSeconds / 2592000);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const TruncatedText = ({ content, onShowMore }: { content: string, onShowMore: () => void }) => {
  // Truncate markdown to 120 characters and append show more
  const truncatedContent = content
    .replace(/\n/g, ' ') // Replace newlines with spaces
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .trim()
    .slice(0, 120)

  return (
    <div className="text-sm text-gray-700 mb-2">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize, rehypeRaw]}
        components={{
          p: ({ children }) => <>{children}</>,
          h1: ({ children }) => <>{children}</>,
          h2: ({ children }) => <>{children}</>,
          h3: ({ children }) => <>{children}</>,
          h4: ({ children }) => <>{children}</>,
          h5: ({ children }) => <>{children}</>,
          h6: ({ children }) => <>{children}</>,
          ul: ({ children }) => <>{children}</>,
          ol: ({ children }) => <>{children}</>,
          li: ({ children }) => <>{children}</>,
          blockquote: ({ children }) => <>{children}</>,
          code: ({ children }) => <>{children}</>,
          pre: ({ children }) => <>{children}</>,
          a: ({ children }) => <>{children}</>,
          img: () => null,
          table: ({ children }) => <>{children}</>,
          thead: ({ children }) => <>{children}</>,
          tbody: ({ children }) => <>{children}</>,
          tr: ({ children }) => <>{children}</>,
          th: ({ children }) => <>{children}</>,
          td: ({ children }) => <>{children}</>,
        }}
      >
        {truncatedContent}
      </ReactMarkdown>
      <span 
        className="text-primary-base hover:text-primary-dark font-medium cursor-pointer hover:no-underline"
        onClick={(e) => {
          e.stopPropagation()
          onShowMore()
        }}
      >
        {' '}...Show more
      </span>
    </div>
  )
}

export const CommunityUpdates: React.FC<CommunityUpdatesProps> = ({ communityId }) => {
  const breakpoint = useBreakpoint();
  const [updates, setUpdates] = useState<CommunityUpdate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [selectedUpdate, setSelectedUpdate] = useState<CommunityUpdate | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { initialGridSize, gridPageSize, gridColsClass } = useMemo(() => {
    if (breakpoint === 'xs' || breakpoint === 'sm' || breakpoint === 'md') {
      return { initialGridSize: 2, gridPageSize: 2, gridColsClass: 'md:grid-cols-2' };
    } 
    return { initialGridSize: 3, gridPageSize: 3, gridColsClass: 'lg:grid-cols-3' }; 
  }, [breakpoint]);

  const INITIAL_LOAD_SIZE = 1 + initialGridSize; // 1 featured + dynamic grid items

  useEffect(() => {
    setUpdates([]);
    setPage(0);
    setHasMore(true);
    fetchUpdates(0, true); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId, initialGridSize]); // Re-fetch if communityId or initialGridSize (due to breakpoint) changes

  const fetchUpdates = async (pageNum: number, isInitialLoad = false) => {
    setLoading(true);
    setError(null);
    try {
      const pageSize = isInitialLoad ? INITIAL_LOAD_SIZE : gridPageSize;
      // For subsequent loads (not initial), the offset is after the initial full load (featured + initial grid)
      // plus items from previous "load more" pages.
      const from = isInitialLoad ? 0 : INITIAL_LOAD_SIZE + ((pageNum -1) * gridPageSize);
      const to = from + pageSize - 1;

      const { data, error, count } = await supabase
        .from('community_updates')
        .select(`
          id, title, author_id, visibility, is_pinned, content, created_at, updated_at, image,
          author:author_id ( first_name, last_name, email, profile_image_url )
        `, { count: 'exact' })
        .eq('community_id', communityId)
        .neq('visibility', 'draft')
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;

      const mapped = (data || []).map((update: any) => ({
        ...update,
        author: Array.isArray(update.author) ? update.author[0] : update.author
      }));
      
      setUpdates(prev => isInitialLoad ? mapped : [...prev, ...mapped]);
      const totalFetchedSoFar = isInitialLoad ? mapped.length : updates.length + mapped.length;
      setHasMore(count ? count > totalFetchedSoFar : mapped.length === pageSize);

    } catch (error) {
      captureAppException(error, {
        tags: {
          context: 'community_updates',
          action: 'fetch_updates',
          community_id: communityId,
          page_num: pageNum,
          is_initial_load: isInitialLoad
        }
      });
      setError('Failed to fetch updates. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchUpdates(nextPage, false);
    
    captureCustomEvent('community_updates_load_more', {
      eventProperties: {
        community_id: communityId,
        page: nextPage,
        current_updates_count: updates.length
      }
    });
  };

  const openUpdateModal = (update: CommunityUpdate) => {
    setSelectedUpdate(update);
    setIsModalOpen(true);
    
    captureCustomEvent('community_update_modal_opened', {
      eventProperties: {
        community_id: communityId,
        update_id: update.id,
        update_visibility: update.visibility,
        is_pinned: update.is_pinned
      }
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedUpdate(null);
  };

  const renderUpdateCard = (update: CommunityUpdate, isFeatureCard = false) => {
    const authorName = update.author?.first_name || update.author?.last_name 
      ? `${update.author?.first_name || ''} ${update.author?.last_name || ''}`.trim() 
      : (update.author?.email || update.author_id);

    const visibilityPill = update.visibility === 'public' ? (
      <Pill
        text="Public"
        className="bg-green-100 text-green-700 flex items-center gap-1 text-xs py-1"
        icon={<GlobeAltIcon className="w-3 h-3" />}
      />
    ) : update.visibility === 'restricted' ? (
      <Pill
        text="Members Only"
        className="bg-blue-100 text-blue-700 flex items-center gap-1 text-xs py-1"
        icon={<LockClosedIcon className="w-3 h-3" />}
      />
    ) : (
      <Pill
        text="Draft"
        className="bg-gray-100 text-gray-700 text-xs py-1"
      />
    );

    if (isFeatureCard) {
      return (
        <div className="cursor-pointer" onClick={() => openUpdateModal(update)}>
          <BaseCard className={'mb-8 bg-secondary-50'}>
            <div className="p-4 md:p-4 sm:p-2">
              <div className="flex items-center gap-2 mb-2"> 
                <Avatar
                  firstName={update.author?.first_name || ''}
                  lastName={update.author?.last_name || ''}
                  size={"small"} 
                />
                <span className="font-bold truncate">{authorName}</span>
              </div>

              <div className="text-sm text-gray-500 mb-1 flex items-center gap-1">
                <ShieldCheckIcon className="h-4 w-4 text-primary-base" /> 
                <span>Community Manager</span>
              </div>

              <div className="text-xs text-gray-500 mb-1" title={new Date(update.created_at).toLocaleString()}>
                {formatRelativeTime(update.created_at)}
              </div>

              <div className="my-3">
                {visibilityPill} 
              </div>
              
              <div className="line-clamp-4 prose prose-slate max-w-none [&_ul]:!list-none [&_ol]:!list-none prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-ul:list-disc prose-ol:list-decimal prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-code:text-primary-dark prose-table:border-collapse prose-img:rounded-lg prose-a:text-primary-base hover:prose-a:text-primary-dark prose-hr:border-gray-300">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[
                    rehypeRaw,
                    [rehypeSanitize, customSchema]
                  ]}
                >
                  {update.content}
                </ReactMarkdown>
              </div>

              <div className="flex items-center gap-1 text-sm">
                <span className="text-gray-500">...</span>
                <button
                  className="text-primary-base hover:text-primary-dark font-medium"
                  onClick={(e) => {
                    e.stopPropagation();
                    openUpdateModal(update);
                  }}
                >
                  Show more
                </button>
              </div>

              {update.image && (
                <div className="w-full aspect-[16/9] mt-4 overflow-hidden rounded-lg">
                  <img 
                    src={getPublicUrl({ bucket: 'community_images', path: update.image }) || ''} 
                    alt={update.title || 'Post image'}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
            </div>
          </BaseCard>
        </div>
      );
    }

    // Small grid card
    return (
      <div 
        className={'cursor-pointer transition-transform hover:scale-[1.01] h-full'}
        onClick={() => openUpdateModal(update)}
      >
        <BaseCard className={'bg-greyscale-200 h-full flex flex-col'}>
          <div className="p-4 md:p-2 sm:p-2 flex flex-col flex-grow">
            <div className="flex items-center gap-2 mb-2"> 
              <Avatar
                firstName={update.author?.first_name || ''}
                lastName={update.author?.last_name || ''}
                size={"small"} 
              />
              <span className="font-bold truncate">{authorName}</span>
            </div>

            <div className="text-sm text-gray-500 mb-1 flex items-center gap-1">
              <ShieldCheckIcon className="h-4 w-4 text-primary-base" /> 
              <span>Community Manager</span>
            </div>

            <div className="text-xs text-gray-500 mb-1" title={new Date(update.created_at).toLocaleString()}>
              {formatRelativeTime(update.created_at)}
            </div>

            <div className="my-3">
               {visibilityPill} 
            </div>
            
            <div className="flex-grow">
              <TruncatedText 
                content={update.content}
                onShowMore={() => openUpdateModal(update)}
              />
            </div>

            {update.image && (
              <div className="w-full aspect-[16/9] mt-4 overflow-hidden rounded-lg">
                <img 
                  src={getPublicUrl({ bucket: 'community_images', path: update.image }) || ''} 
                  alt={update.title || 'Post image'}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>
        </BaseCard>
      </div>
    );
  };

  const featuredUpdate = updates.length > 0 ? updates[0] : null;
  const gridUpdates = updates.length > 1 ? updates.slice(1) : [];

  return (
    <>
      <BaseCard>
        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">Community Updates</h2>
        </div>
        <div>
          {error && <div className="text-red-600 bg-red-50 p-3 rounded mb-4">{error}</div>}
          {!loading && updates.length === 0 && !error && (
            <div className="text-gray-600">No updates yet.</div>
          )}
          
          {featuredUpdate && (
            <div key={`featured-${featuredUpdate.id}`} id={`update-${featuredUpdate.id}`} className="scroll-mt-6">
              {renderUpdateCard(featuredUpdate, true)}
            </div>
          )}
          
          {gridUpdates.length > 0 && (
            <div className={`grid grid-cols-1 md:grid-cols-2 ${gridColsClass} gap-4 mt-6`}>
              {gridUpdates.map(update => (
                <div key={update.id} id={`update-${update.id}`} className="scroll-mt-6">
                  {renderUpdateCard(update)}
                </div>
              ))}
            </div>
          )}

          {loading && page > 0 && <div className="text-center py-4">Loading more updates...</div>} 
          {loading && page === 0 && updates.length === 0 && !error && <div className="text-center py-4">Loading updates...</div>} 


          {hasMore && !loading && (
            <div className="flex justify-center mt-8">
              <Button onClick={handleLoadMore} loading={loading} disabled={loading} variant="outline" size="small">
                Load More
              </Button>
            </div>
          )}
        </div>
      </BaseCard>

      {/* Modal for displaying full update */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        size="2xl"
      >
        <div className="p-4">
          {selectedUpdate && (
            <>
              <div className="flex gap-3 mb-4">
                <div className="flex-shrink-0">
                  <Avatar
                    firstName={selectedUpdate.author?.first_name || ''}
                    lastName={selectedUpdate.author?.last_name || ''}
                    size="medium"
                  />
                </div>
                
                <div className="flex-1">
                  <div className="flex justify-between items-center mb-1">
                    <div className="font-bold">
                      {selectedUpdate.author?.first_name || selectedUpdate.author?.last_name 
                        ? `${selectedUpdate.author?.first_name || ''} ${selectedUpdate.author?.last_name || ''}`.trim() 
                        : (selectedUpdate.author?.email || selectedUpdate.author_id)}
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedUpdate.is_pinned && <span className="text-xs px-2 py-1 bg-primary-base text-white rounded">Pinned</span>}
                      <span 
                        className="text-xs text-gray-500"
                        title={new Date(selectedUpdate.created_at).toLocaleString()}
                      >
                        {formatRelativeTime(selectedUpdate.created_at)}
                      </span>
                      {selectedUpdate.visibility === 'public' ? (
                        <Pill
                          text="Public"
                          className="bg-green-100 text-green-700 flex items-center gap-1 text-xs py-1"
                          icon={<GlobeAltIcon className="w-3 h-3" />}
                        />
                      ) : selectedUpdate.visibility === 'restricted' ? (
                        <Pill
                          text="Members Only"
                          className="bg-blue-100 text-blue-700 flex items-center gap-1 text-xs py-1"
                          icon={<LockClosedIcon className="w-3 h-3" />}
                        />
                      ) : (
                        <Pill
                          text="Draft"
                          className="bg-gray-100 text-gray-700 text-xs py-1"
                        />
                      )}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <ShieldCheckIcon className="h-4 w-4 text-primary-base" />
                      <span>Community Manager</span>
                    </div>
                  </div>
                </div>
              </div>

              <BaseCard className="bg-greyscale-200">
                <div className="p-4">
                  <div className="prose prose-slate max-w-none mb-4 [&_ul]:!list-none [&_ol]:!list-none prose-headings:font-bold prose-h1:text-3xl prose-h2:text-2xl prose-h3:text-xl prose-ul:list-disc prose-ol:list-decimal prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-code:text-primary-dark prose-table:border-collapse prose-img:rounded-lg prose-a:text-primary-base hover:prose-a:text-primary-dark prose-hr:border-gray-300">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[
                        rehypeRaw,
                        [rehypeSanitize, customSchema]
                      ]}
                    >
                      {selectedUpdate.content}
                    </ReactMarkdown>
                  </div>
                  {selectedUpdate.image && (
                    <div className="w-full aspect-[16/9] overflow-hidden rounded-lg">
                      <img 
                        src={getPublicUrl({ bucket: 'community_images', path: selectedUpdate.image }) || ''} 
                        alt={selectedUpdate.title || 'Post image'}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
              </BaseCard>

              <div className="flex justify-end mt-4">
                <Button variant="outline" size="small" onClick={closeModal}>Close</Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
};